// Creating and editing a data model of your own (a "virtual model"): the pure rules of the editor, so they can be tested.
// The server accepts a name of 1–100 characters and 1–100 fields of up to 50 sources each; the editor is stricter on top
// (labels required and unique, every field needs a source) because a field without a source finds nothing.
import { humanize } from './format.ts';

export const LIMITS = { name: 100, fields: 100, label: 100, sources: 50 } as const;

export interface Source { cls: string; path: string; system?: string }
export interface EditField { label: string; paths: Source[] }
export interface EditModel { name: string; fields: EditField[] }
/** What the editor keeps in sessionStorage while it is open. */
export interface EditDraft { name: string; fields: EditField[] }

export const emptyField = (label = ''): EditField => ({ label, paths: [] });
export const emptyModel = (): EditModel => ({ name: '', fields: [emptyField()] });

export const sourceKey = (s: Pick<Source, 'cls' | 'path'>) => `${s.cls}·${s.path}`;

/** The system a class belongs to, as far as the name tells: ErpCustomer → ERP, Maventa… → MAV. */
export function systemOfClass(cls: string): string {
  const first = (/^[A-Z]?[a-z0-9]+|^[A-Z]+(?![a-z])/.exec(cls.replace(/^[^A-Za-z0-9]+/, ''))?.[0] ?? cls).toUpperCase();
  return first.length <= 4 ? first : first.slice(0, 3);
}

/** A label for a field, suggested from its last path segment: Customer/BillingAddress/Name → Name; cust_name → Cust name. */
export function suggestLabel(path: string): string {
  const leaf = path.split(/[/.]/).map((s) => s.replace(/\[\d*\]$/, '').replace(/^@/, '')).filter(Boolean).pop() ?? path;
  const h = humanize(leaf);
  return (h.charAt(0).toUpperCase() + h.slice(1)).slice(0, LIMITS.label);
}

export interface FieldProblems { label?: string; sources?: string }
export interface Problems { name?: string; fields?: string; perField: FieldProblems[]; ok: boolean }

/** The same rules as the server plus the editor's own, with a message for each problem. */
export function validate(m: EditModel): Problems {
  const out: Problems = { perField: m.fields.map(() => ({})), ok: true };
  const name = m.name.trim();
  if (!name) out.name = 'Give the data model a name';
  else if (name.length > LIMITS.name) out.name = `At most ${LIMITS.name} characters (now ${name.length})`;
  if (m.fields.length === 0) out.fields = 'Add at least one field';
  else if (m.fields.length > LIMITS.fields) out.fields = `At most ${LIMITS.fields} fields (now ${m.fields.length})`;
  const seen = new Map<string, number>();
  m.fields.forEach((f, i) => {
    const label = f.label.trim();
    const p = out.perField[i]!;
    if (!label) p.label = 'Give the field a label';
    else if (label.length > LIMITS.label) p.label = `At most ${LIMITS.label} characters (now ${label.length})`;
    else if (seen.has(label.toLowerCase())) p.label = 'Another field already has this label';
    else seen.set(label.toLowerCase(), i);
    if (f.paths.length === 0) p.sources = 'Add at least one source field';
    else if (f.paths.length > LIMITS.sources) p.sources = `At most ${LIMITS.sources} source fields (now ${f.paths.length})`;
  });
  out.ok = !out.name && !out.fields && out.perField.every((p) => !p.label && !p.sources);
  return out;
}

/** How many problems there are, for "Fix 2 things to save". */
export const problemCount = (p: Problems) => (p.name ? 1 : 0) + (p.fields ? 1 : 0) + p.perField.reduce((n, f) => n + (f.label ? 1 : 0) + (f.sources ? 1 : 0), 0);

/** The body for POST …/virtual-models: names and labels trimmed, sources as they are. */
export function buildPayload(id: string | undefined, m: EditModel): { id?: string; name: string; fields: EditField[] } {
  return {
    ...(id ? { id } : {}),
    name: m.name.trim(),
    fields: m.fields.map((f) => ({
      label: f.label.trim(),
      paths: f.paths.map((p) => ({ cls: p.cls, path: p.path, ...(p.system ? { system: p.system } : {}) })),
    })),
  };
}

export const hasSource = (f: EditField, s: Pick<Source, 'cls' | 'path'>) => f.paths.some((p) => p.cls === s.cls && p.path === s.path);

const replaceAt = <T>(xs: T[], i: number, x: T): T[] => xs.map((v, k) => (k === i ? x : v));

/** The fields with the source added to field `i`; unchanged when it is already there or the field does not exist. */
export function addSource(fields: EditField[], i: number, s: Source): EditField[] {
  const f = fields[i];
  if (!f || hasSource(f, s) || f.paths.length >= LIMITS.sources) return fields;
  return replaceAt(fields, i, { ...f, paths: [...f.paths, { cls: s.cls, path: s.path, system: s.system ?? systemOfClass(s.cls) }] });
}

export function removeSource(fields: EditField[], i: number, k: number): EditField[] {
  const f = fields[i];
  return f ? replaceAt(fields, i, { ...f, paths: f.paths.filter((_, n) => n !== k) }) : fields;
}

export function setLabel(fields: EditField[], i: number, label: string): EditField[] {
  const f = fields[i];
  return f ? replaceAt(fields, i, { ...f, label }) : fields;
}

/** Moves the item at `from` by `delta` places (−1 up, +1 down); at the ends nothing moves. */
export function move<T>(xs: T[], from: number, delta: number): T[] {
  const to = from + delta;
  if (from < 0 || from >= xs.length || to < 0 || to >= xs.length) return xs;
  const out = [...xs];
  const [x] = out.splice(from, 1);
  out.splice(to, 0, x!);
  return out;
}

export const removeField = (fields: EditField[], i: number): EditField[] => fields.filter((_, n) => n !== i);

/** The editable copy of a saved model. */
export const toEdit = (m: { name: string; fields: EditField[] }): EditModel => ({ name: m.name, fields: m.fields.map((f) => ({ label: f.label, paths: f.paths.map((p) => ({ ...p })) })) });

export type Target = { field: number } | { label: string };

export interface Added { fields: EditField[]; result: 'added' | 'exists' | 'full' | 'label-taken' | 'no-field'; field?: string }

/**
 * Adds a source to an existing field of a model (`{ field }`) or as a field of its own (`{ label }`): "Add to my data model…".
 * A source that is already one of that field is not added again ('exists'); a new field's label must not be taken.
 */
export function addToModel(fields: EditField[], target: Target, s: Source): Added {
  if ('field' in target) {
    const f = fields[target.field];
    if (!f) return { fields, result: 'no-field' };
    if (hasSource(f, s)) return { fields, result: 'exists', field: f.label };
    if (f.paths.length >= LIMITS.sources) return { fields, result: 'full', field: f.label };
    return { fields: addSource(fields, target.field, s), result: 'added', field: f.label };
  }
  const label = target.label.trim();
  if (fields.some((f) => f.label.trim().toLowerCase() === label.toLowerCase())) return { fields, result: 'label-taken', field: label };
  return { fields: [...fields, { label, paths: [{ cls: s.cls, path: s.path, system: s.system ?? systemOfClass(s.cls) }] }], result: 'added', field: label };
}

// ---------------------------------------------------------------- the URL

/** `?edit=new` or `?edit=<model id>`: the editor open on a new model or on a model's draft. */
export const EDIT_KEY = 'edit';
export const NEW_MODEL = 'new';
export const readEdit = (p: URLSearchParams): string | undefined => p.get(EDIT_KEY) || undefined;
export function withEdit(p: URLSearchParams, id: string | undefined): URLSearchParams {
  const out = new URLSearchParams(p);
  if (id) out.set(EDIT_KEY, id); else out.delete(EDIT_KEY);
  return out;
}

/**
 * `?edit=new&from=<suggestion id>`: the editor open on a new model pre-filled from a suggested data model. Opening it is
 * one history push that writes both; closing it removes both.
 */
export const FROM_KEY = 'from';
export const readFrom = (p: URLSearchParams): string | undefined => (p.get(EDIT_KEY) === NEW_MODEL && p.get(FROM_KEY)) || undefined;
export function withFrom(p: URLSearchParams, id: string | undefined): URLSearchParams {
  const out = new URLSearchParams(p);
  if (id) { out.set(EDIT_KEY, NEW_MODEL); out.set(FROM_KEY, id); } else { out.delete(EDIT_KEY); out.delete(FROM_KEY); }
  return out;
}
/** The draft key of a new model started from a suggestion (one per suggestion, apart from a plain new model). */
export const suggestionDraftId = (id: string) => `new-${id}`;

/** A suggestion as the editor's starting point: its name and fields, every source with its system. */
export function fromSuggestion(s: { name: string; fields: { label: string; sources: { cls: string; path: string; system: string | null }[] }[] }): EditModel {
  return {
    name: s.name.slice(0, LIMITS.name),
    fields: s.fields.slice(0, LIMITS.fields).map((f) => ({
      label: f.label.slice(0, LIMITS.label),
      paths: f.sources.slice(0, LIMITS.sources).map((x) => ({ cls: x.cls, path: x.path, system: x.system ?? systemOfClass(x.cls) })),
    })),
  };
}

/** `?addto=<field path>`: the "Add to my data model" window of a field of the discovered model. */
export const ADD_KEY = 'addto';
export const readAdd = (p: URLSearchParams): string | undefined => p.get(ADD_KEY) || undefined;
export function withAdd(p: URLSearchParams, path: string | undefined): URLSearchParams {
  const out = new URLSearchParams(p);
  if (path) out.set(ADD_KEY, path); else out.delete(ADD_KEY);
  return out;
}

/** The editor's sessionStorage key: one draft per model (and one for a new model) of a tenant. */
export const draftKey = (tid: string, id: string) => `modeledit.${tid}.${id}`;
export const addDraftKey = (tid: string, cls: string, path: string) => `modeladd.${tid}.${cls}.${path}`;

/** Pick matches for the source picker: every word typed must be in the class, its noun or the path. */
export function filterFields<T extends { cls: string; path: string }>(all: T[], query: string, cls: string | undefined, limit: number): { shown: T[]; total: number } {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  const hit = all.filter((f) => (!cls || f.cls === cls) && words.every((w) => `${f.cls} ${f.path}`.toLowerCase().includes(w)));
  return { shown: hit.slice(0, limit), total: hit.length };
}
